import React, { useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

type Props = {
	previewProductLogo: React.JSX.Element;
	/**
	 * Whether the preview card is loading.
	 */
	isLoading?: boolean;
	/**
	 * Whether this is the preview card that is currently selected in the staging area.
	 */
	isSelected: boolean;
	/**
	 * Whether the preview card is enabled and can be interacted with.
	 * E.g. no cards in the staging area can be enabled.
	 */
	isEnabled?: boolean;
	/**
	 * Whether the suggested work item has been created.
	 */
	isCreated?: boolean;
	/**
	 * A FormattedMessage to display in the header of the preview card.
	 */
	previewHeaderTitle: React.ReactNode;
	/**
	 * Anything to display in the content area of the preview card.
	 */
	previewContent?: React.ReactNode;
	/**
	 * Any actions or content to display in the footer of the preview card.
	 */
	previewFooterActions?: React.ReactNode;
	/**
	 * A test ID to be used for testing purposes.
	 */
	testId?: string;
	/**
	 * A function to call when the card is clicked.
	 */
	onCardClick?: () => void;
	/**
	 * Whether the preview card is for a single work item.
	 */
	isSingleWorkItem?: boolean;
	/**
	 * Whether the preview card content can be collapsed from the header.
	 */
	isCollapsible?: boolean;
	/**
	 * Whether this preview card has no padding.
	 */
	noPadding?: boolean;
};

const messages = defineMessages({
	expandSuggestions: {
		id: 'conversation-assistant.common.ui.base-preview-card.expand-suggestion',
		defaultMessage: 'Expand suggestion',
		description: 'Label for the expand suggestion button',
	},
	collapseSuggestions: {
		id: 'conversation-assistant.common.ui.base-preview-card.collapse-suggestion',
		defaultMessage: 'Collapse suggestion',
		description: 'Label for the collapse suggestion button',
	},
});

// Redefining so we can import the width from the common constants file
const styles = cssMap({
	pressable: {
		width: '100%',
		display: 'block',
		textAlign: 'left',
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	containerBase: {
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
		'&:hover': {
			boxShadow: token('elevation.shadow.overlay'),
		},
	},
	layoutPadding: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
	},
	headerLayoutPadding: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.100'),
	},
	contentPadding: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
	},
	multiWorkItemContentPadding: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	containerTransparentBorder: {
		borderColor: token('color.border'),
	},
	containerWithBorder: {
		borderColor: token('color.border.focused'),
	},
	header: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	footer: {
		width: '100%',
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
});

const Header = ({
	isExpanded,
	isCreated,
	onExpandClick,
	previewProductLogo,
	previewHeaderTitle,
	isSingleWorkItem = false,
}: {
	isExpanded: boolean;
	isCreated: boolean;
	onExpandClick: () => void;
	previewProductLogo: React.JSX.Element;
	previewHeaderTitle: React.ReactNode;
	isSingleWorkItem: boolean;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Inline
			alignBlock="center"
			spread="space-between"
			xcss={cx(isExpanded && styles.header, styles.headerLayoutPadding)}
		>
			<Inline alignBlock="center" space="space.075">
				<Tile label="" size="medium" hasBorder backgroundColor="transparent">
					{previewProductLogo}
				</Tile>
				{previewHeaderTitle}
			</Inline>
			{/* Expand button is always visible for multi work items, but for single work items only when issue is not created */}
			{(!isSingleWorkItem || !isCreated) && (
				<IconButton
					icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
					label={
						isExpanded
							? formatMessage(messages.collapseSuggestions)
							: formatMessage(messages.expandSuggestions)
					}
					onClick={onExpandClick}
					appearance="subtle"
					spacing="compact"
					testId="conversation-assistant.base-preview-card.expand-collapse-button"
					isTooltipDisabled={false}
					// Prevent default onMouseDown behaviour so that the button doesn't remained focused after we click on it. This is so that
					// if a user hovers off the header after clicking these buttons, the buttons are not visible - before, they would remain
					// visible as they were still in focus.
					onMouseDown={(e) => e.preventDefault()}
				/>
			)}
		</Inline>
	);
};

const HeaderWithWrapper = ({
	onHeaderClick,
	isCreated,
	...props
}: {
	isExpanded: boolean;
	isCreated: boolean;
	onHeaderClick?: () => void;
	onExpandClick: () => void;
	previewProductLogo: React.JSX.Element;
	previewHeaderTitle: React.ReactNode;
	isSingleWorkItem: boolean;
}) => {
	if (isCreated && onHeaderClick) {
		return (
			<Pressable onClick={onHeaderClick} xcss={styles.pressable}>
				<Header isCreated={isCreated} {...props} />
			</Pressable>
		);
	}
	return <Header isCreated={isCreated} {...props} />;
};

/**
 * The skeleton of the preview card, with the header, content and footer.
 * Allows different experiences to pass their own content and actions.
 */
export const BasePreviewCard = ({
	previewProductLogo,
	isLoading = false,
	isSelected,
	isEnabled = true,
	isCreated = false,
	previewHeaderTitle,
	previewContent,
	previewFooterActions,
	testId,
	onCardClick,
	isSingleWorkItem = false,
	isCollapsible = true,
	noPadding = false,
}: Props): React.JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(!isCreated);
	const isFirstRender = useRef(true);

	// Update isExpanded when defaultIsExpanded changes on subsequent renders
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		// If the work item has been created, we don't want to expand the preview card by default
		setIsExpanded(!isCreated);
	}, [isCreated]);

	const hasFocusBorder = isSelected && isEnabled;

	const onExpandClick = () => {
		setIsExpanded?.(!isExpanded);
	};

	return (
		<Box
			xcss={cx(
				styles.containerBase,
				hasFocusBorder ? styles.containerWithBorder : styles.containerTransparentBorder,
			)}
			tabIndex={
				FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
					fireExperimentExposure: false,
				})
					? undefined
					: 0
			}
			testId={testId}
		>
			<Stack>
				{isCollapsible ? (
					<HeaderWithWrapper
						isExpanded={isExpanded}
						isCreated={isCreated}
						onHeaderClick={onCardClick}
						onExpandClick={onExpandClick}
						previewProductLogo={previewProductLogo}
						previewHeaderTitle={previewHeaderTitle}
						isSingleWorkItem={isSingleWorkItem}
					/>
				) : (
					<Inline
						alignBlock="start"
						space="space.075"
						xcss={cx(styles.header, styles.layoutPadding)}
					>
						{previewProductLogo}
						<Text size="medium" weight="bold">
							{previewHeaderTitle}
						</Text>
					</Inline>
				)}

				{isExpanded && (!isSingleWorkItem || !isCreated) && isCollapsible && (
					<Stack>
						{/* Body content */}
						<Stack
							xcss={
								!noPadding &&
								(!isSingleWorkItem ? styles.multiWorkItemContentPadding : styles.contentPadding)
							}
						>
							{isLoading ? <Skeleton width="100%" height="24px" /> : previewContent}
						</Stack>

						{/* Footer content */}
						{previewFooterActions && (
							<Inline
								alignBlock="end"
								alignInline="end"
								xcss={cx(styles.footer, styles.layoutPadding)}
							>
								{previewFooterActions}
							</Inline>
						)}
					</Stack>
				)}
			</Stack>
		</Box>
	);
};
